import { useState } from 'react'

import { Card } from '@taro/cards'
import { Stack, Surface, Text } from '@taro/design'
import { haptic } from '@taro/tg'

import type { Card as CardData } from '../api'
import styles from './Deck.module.css'

const SUITS: Record<string, string> = {
  wands: 'Жезлы',
  cups: 'Кубки',
  swords: 'Мечи',
  pentacles: 'Пентакли',
}

/**
 * Каталог всех 78 карт.
 *
 * Открытыми, а не рубашкой вверх: это справочник, а не расклад.
 * Прятать карты здесь означало бы имитировать таинственность там,
 * где человек пришёл читать.
 */
export function Deck({ cards }: { cards: CardData[] }) {
  const [open, setOpen] = useState<CardData | null>(null)

  if (open) {
    return <CardDetail card={open} onBack={() => setOpen(null)} />
  }

  const majors = cards.filter((c) => c.arcana === 'major')
  const groups = Object.keys(SUITS).map((suit) => ({
    title: SUITS[suit]!,
    cards: cards.filter((c) => c.suit === suit),
  }))

  return (
    <Stack gap={8}>
      <Group
        title="Старшие арканы"
        note="Двадцать две карты о том, что происходит с человеком, а не вокруг него"
        cards={majors}
        onPick={setOpen}
      />
      {groups.map((g) => (
        <Group key={g.title} title={g.title} cards={g.cards} onPick={setOpen} />
      ))}
    </Stack>
  )
}

function Group({
  title,
  note,
  cards,
  onPick,
}: {
  title: string
  note?: string
  cards: CardData[]
  onPick: (c: CardData) => void
}) {
  return (
    <section>
      <Text variant="title-s" as="h2">
        {title}
      </Text>
      {note ? (
        <Text variant="body-s" tone="tertiary" measure style={{ marginTop: 'var(--sp-1)' }}>
          {note}
        </Text>
      ) : null}

      <div className={styles.grid}>
        {cards.map((c) => (
          <button
            key={c.slug}
            type="button"
            className={styles.cell}
            onClick={() => {
              haptic('select')
              onPick(c)
            }}
          >
            <Card back="/card-back.webp" face={c.image} label={c.name} size="md" revealed />
            <Text variant="caption" tone="tertiary" className={styles.caption}>
              {c.name}
            </Text>
          </button>
        ))}
      </div>
    </section>
  )
}

function CardDetail({ card, onBack }: { card: CardData; onBack: () => void }) {
  return (
    <Stack gap={6} align="center">
      <Card back="/card-back.webp" face={card.image} label={card.name} size="hero" revealed />

      <Stack gap={2} align="center">
        <Text variant="overline" tone="sacred">
          {card.arcana === 'major'
            ? `Старший аркан · ${card.number}`
            : `${SUITS[card.suit ?? ''] ?? ''}`}
        </Text>
        <Text variant="display-m" as="h1" style={{ textAlign: 'center' }}>
          {card.name}
        </Text>
      </Stack>

      <Surface level="sunken" className={styles.meanings}>
        <Stack gap={5}>
          <Stack gap={2}>
            <Text variant="overline" tone="tertiary">
              Прямое положение
            </Text>
            <Text variant="body-l" tone="secondary" measure>
              {card.upright}
            </Text>
          </Stack>

          <Stack gap={2}>
            <Text variant="overline" tone="tertiary">
              Перевёрнутое
            </Text>
            <Text variant="body-l" tone="secondary" measure>
              {card.reversed}
            </Text>
          </Stack>
        </Stack>
      </Surface>

      <div className="oracle">
        <div className="oracle__body">
          <p>{card.description}</p>
        </div>
      </div>

      <button type="button" className={styles.back} onClick={onBack}>
        ← Ко всей колоде
      </button>
    </Stack>
  )
}
